import {$qsAll, $qs} from '../../@shared/ts/dom';
import {captureSentryException} from '../../@shared/ts/sentry';
import {type TransactionUpdate} from '../../@shared/ts/transaction';
import {FeatureFlag} from '../../@type/features';
import {type IResponse} from '../../@type/response';
import {Feature} from './features';

async function createPaymentTransaction(gateway: string): Promise<string | null> {
	const createTransactionURL = Feature.metadata<string>(FeatureFlag.NATIVE_CHECKOUT, 'create_transaction_url');
	if (!createTransactionURL) {
		captureSentryException(new Error('Invalid or missing create transaction URL'));
		return null;
	}

	try {
		const formData = new FormData();
		formData.append('gateway_id', gateway);
		formData.append('checkout_location', 'checkout-page');

		const response = await fetch(createTransactionURL, {
			method: 'POST',
			body: formData,
		});

		const body = await response.json() as IResponse<{transaction_id: string}>;

		if (!response.ok || !body.success) {
			return null;
		}

		return body.data.transaction_id;
	} catch (error) {
		return null;
	}
}

async function updatePaymentTransaction(transactionId: string, options: TransactionUpdate): Promise<boolean> {
	const updateTransactionURL = Feature.metadata<string>(FeatureFlag.NATIVE_CHECKOUT, 'update_transaction_url');
	if (!updateTransactionURL) {
		captureSentryException(new Error('Invalid or missing update transaction URL'), {
			transaction_id: transactionId,
		});

		return false;
	}

	try {
		const formData = new FormData();
		formData.append('transaction_id', transactionId);

		if (options.paymentStatus) {
			formData.append('payment_status', options.paymentStatus);
		}

		if (options.orderStatus) {
			formData.append('order_status', options.orderStatus);
		}

		if (options.note) {
			formData.append('note', options.note);
		}

		const response = await fetch(updateTransactionURL, {
			method: 'POST',
			body: formData,
		});

		const responseBody = await response.json() as IResponse<{transaction_id: string}>;

		if (!response.ok || !responseBody.success) {
			return false;
		}

		return true;
	} catch (error) {
		return false;
	}
}

function insertTransactionFields($checkoutForm: HTMLFormElement, transactionId: string) {
	const $transactionId = document.createElement('input');
	$transactionId.type = 'hidden';
	$transactionId.name = 'peachpay_transaction_id';
	$transactionId.value = transactionId;
	$checkoutForm.insertAdjacentElement('beforeend', $transactionId);
}

function cleanupTransactionFields() {
	$qsAll('input[name="peachpay_transaction_id"]', $el => {
		$el.remove();
	});
}

function retrieveTransactionId(): string | null {
	return $qs<HTMLInputElement>('input[name="peachpay_transaction_id"]')?.value ?? null;
}

export {
	createPaymentTransaction,
	updatePaymentTransaction,
	insertTransactionFields,
	cleanupTransactionFields,
	retrieveTransactionId,

};
